import type { CreditsData, UserSettings } from '@/api/auth';

/**
 * Header / ticket active balance — matches legacy
 * `UpdateCredits` → `BindUserCredits`:
 * withdrawable + restricted + sportsbook bonus + casino bonus + external SB bonus.
 */
export function activeBalanceTotal(credits: CreditsData | null | undefined): number {
  if (!credits) return 0;
  return (
    (credits.WithdrawableCredits ?? 0) +
    (credits.RestrictedCredits ?? 0) +
    (credits.BonusCredits ?? 0) +
    (credits.CasinoBonusCredits ?? 0) +
    (credits.ExternalSportsbookBonusCredits ?? 0)
  );
}

/**
 * Main wallet total — matches legacy `maincredits`:
 * WithdrawableCredits + RestrictedCredits.
 */
export function mainBalanceTotal(credits: CreditsData | null | undefined): number {
  if (!credits) return 0;
  return (credits.WithdrawableCredits ?? 0) + (credits.RestrictedCredits ?? 0);
}

export type BalanceBreakdownRowId =
  | 'main'
  | 'sportsBonus'
  | 'casinoBonus'
  | 'externalSportsbook'
  | 'withdrawable'
  | 'restricted';

export interface BalanceBreakdownRow {
  id: BalanceBreakdownRowId;
  /** Default English label; callers may translate via i18n. */
  label: string;
  amount: number;
  formatted: string;
}

function currencyLabel(user: UserSettings | null | undefined): string {
  const raw = user?.Currency ?? user?.CurrCode ?? user?.CurrencyCode;
  return typeof raw === 'string' ? raw.trim() : '';
}

function decimalPlaces(user: UserSettings | null | undefined): number {
  const value = user?.NumberOfDecimals;
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 2;
}

function formatWithCurrency(value: number, user: UserSettings | null | undefined): string {
  const amount = formatAmount(value, decimalPlaces(user));
  const currency = currencyLabel(user);
  return currency ? `${amount} ${currency}` : amount;
}

/** Formats an amount like legacy `data-format="amount"` (thousands separators). */
export function formatAmount(value: number, decimals = 2): string {
  const safe = Number.isFinite(value) ? value : 0;
  const fixed = safe.toFixed(decimals);
  // Insert commas before every group of three digits left of the decimal point.
  return fixed.replace(/\d(?=(\d{3})+\.)/g, '$&,');
}

/** Player header balance string from LoginResponse user settings. */
export function formatPlayerBalance(user: UserSettings | null | undefined): string {
  return formatWithCurrency(activeBalanceTotal(user?.Credits), user);
}

/** MAIN BALANCE card amount — withdrawable + restricted only. */
export function formatMainBalance(user: UserSettings | null | undefined): string {
  return formatWithCurrency(mainBalanceTotal(user?.Credits), user);
}

/** Ordered rows for the menu balance breakdown card. */
export function playerBalanceBreakdown(
  user: UserSettings | null | undefined,
): BalanceBreakdownRow[] {
  const credits = user?.Credits;
  const rows: Array<{ id: BalanceBreakdownRowId; label: string; amount: number }> = [
    { id: 'main', label: 'Main', amount: mainBalanceTotal(credits) },
    { id: 'sportsBonus', label: 'Sports bonus', amount: credits?.BonusCredits ?? 0 },
    { id: 'casinoBonus', label: 'Casino bonus', amount: credits?.CasinoBonusCredits ?? 0 },
    {
      id: 'externalSportsbook',
      label: 'External sportsbook',
      amount: credits?.ExternalSportsbookBonusCredits ?? 0,
    },
    { id: 'withdrawable', label: 'Withdrawable', amount: credits?.WithdrawableCredits ?? 0 },
    { id: 'restricted', label: 'Restricted', amount: credits?.RestrictedCredits ?? 0 },
  ];

  return rows.map((row) => ({
    ...row,
    formatted: formatWithCurrency(row.amount, user),
  }));
}
